import React, { useEffect, useLayoutEffect, useRef } from 'react';
import isNull from 'lodash/isNull';
import { parse as parseDate } from 'date-fns/parse';
import { isValid as isValidDate } from 'date-fns/isValid';
import { format as formatDate } from 'date-fns/format';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { useCheckOnFocus } from '@hooks/useCheckOnFocus';
import { DateHelper } from '@utils/DateHelper';

type DateChangeEvent = React.SyntheticEvent<any> | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;

interface Params {
    selected?: Nullable<Date>;
    minDate?: Nullable<Date>;
    maxDate?: Nullable<Date>;
    isClearable?: boolean;
    customDateFormat?: string;
    portalId?: string;
    withoutMobileCentering?: boolean;
    name?: string;
    onChange(date: Date, name?: string, event?: DateChangeEvent, ...args: any[]): void;
}

interface Data {
    isOnFocus: boolean;
    handleChange(date: Nullable<Date>, event?: DateChangeEvent, ...args: any[]): void;
    handleClear(): void;
    handleInputBlur(event: React.FocusEvent<HTMLInputElement>): void;
    captureTypedValue(event?: { target?: EventTarget | null }): void;
    handleOpen(): void;
}

export function useDatePicker({
    selected,
    minDate,
    maxDate,
    isClearable,
    customDateFormat,
    portalId,
    withoutMobileCentering,
    name,
    onChange,
}: Params): Data {
    const { isOnFocus, handleFocus, handleBlur } = useCheckOnFocus();

    // DEV-1646: react-datepicker + react-imask теряет onChange при частичном
    // редактировании; к моменту blur react-datepicker сбрасывает input.value на
    // отображение selected. Захватываем введённый текст в ref сразу при keystroke
    // через два независимых источника (onChangeRaw на ReactDatePicker и onInput на
    // customInput) — и используем его на blur вместо event.currentTarget.value.
    const lastTypedValueRef = useRef<string | null>(null);

    const dateFormatString = customDateFormat || 'dd.MM.yyyy';

    useEffect(() => {
        initDataTestParams();
    }, []);

    useLayoutEffect(() => {
        if (!portalId) return;
        let el = document.getElementById(portalId);
        if (!el) {
            el = document.createElement('div');
            el.id = portalId;
            document.body.appendChild(el);
        }
        el.classList.add('date_picker__root-portal');
        // The portal element is shared by id, so pickers with conflicting settings overwrite each other.
        el.classList.toggle('date_picker__withMobileCentering', !withoutMobileCentering);

        // The element is not cleaned up either way, so it seems fine to just leave it
    }, [portalId, withoutMobileCentering]);

    const handleChange = (date: Nullable<Date>, event?: DateChangeEvent, ...args: any[]): void => {
        const value =
            isClearable && isNull(date)
                ? date
                : !isNull(date) && maxDate && DateHelper.isAfter(date, maxDate)
                  ? maxDate
                  : !isNull(date) && minDate && DateHelper.isBefore(date, minDate)
                    ? minDate
                    : date;

        onChange(value as Date, name, event, ...args);
        handleBlur();
    };

    const handleClear = (): void => {
        handleChange(null);
    };

    const captureTypedValue = (event?: { target?: EventTarget | null }): void => {
        const target = event?.target as HTMLInputElement | null | undefined;
        if (target && typeof target.value === 'string') {
            lastTypedValueRef.current = target.value;
        }
    };

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
        const inputValue = lastTypedValueRef.current ?? event.currentTarget?.value ?? '';
        const formattedSelected =
            !!selected && DateHelper.isValid(selected) ? formatDate(selected, dateFormatString) : '';
        if (inputValue && inputValue !== formattedSelected) {
            const parsed = parseDate(inputValue, dateFormatString, new Date());
            if (isValidDate(parsed)) {
                handleChange(parsed, event);
            }
        }
        lastTypedValueRef.current = null;
        handleBlur();
    };

    return {
        isOnFocus,
        handleChange,
        handleClear,
        handleInputBlur,
        captureTypedValue,
        handleOpen: handleFocus,
    };
}

function initDataTestParams(): void {
    document.querySelector('.react-datepicker__navigation--previous')?.setAttribute('data-test', 'datePicker_prev');
    document.querySelector('.react-datepicker__navigation--next')?.setAttribute('data-test', 'datePicker_next');
    document.querySelector('.react-datepicker__day--today')?.setAttribute('data-test', 'datePicker_dayToday');
    document.querySelector('.react-datepicker__day--013')?.setAttribute('data-test', 'datePicker_dayThirteen');
    document.querySelector('.react-datepicker__navigation--previous')?.setAttribute('data-testid', 'datePicker_prev');
    document.querySelector('.react-datepicker__navigation--next')?.setAttribute('data-testid', 'datePicker_next');
    document.querySelector('.react-datepicker__day--today')?.setAttribute('data-testid', 'datePicker_dayToday');
    document.querySelector('.react-datepicker__day--013')?.setAttribute('data-testid', 'datePicker_dayThirteen');
}
